import { useEffect, useState } from "react";
import dayjs from "dayjs";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";

import { subcategories } from "@aclymatepackages/lists";
import {
  formatDate,
  formatDecimal,
  formatDollars
} from "@aclymatepackages/formatters";

import ReviewTableShell from "../review-table/ReviewTableShell";
import FieldConfidence from "../review-table/FieldConfidence";
import AllocationStep from "./AllocationStep";

import {
  DOCUMENT_FAMILY_LABELS,
  UNDOABLE_REVIEW_STATES,
  areValuesLocked,
  claimAgeMs,
  isClaimStale,
  isAllocationSatisfied,
  isInClosedYear,
  isLineActionable,
  isLineReady,
  isManualOriginLine,
  CLAIM_STALE_MS,
  isVendorFieldShown,
  isVendorMatchAccepted,
  lineEntityType,
  lineGateFailures,
  lineKeyFor,
  readLine,
  reasonCopy,
  unclearedLowFields,
  withDerivedRequirements
} from "./reviewLineGates";
import { csvDateValue, duplicateAddedOn } from "./csvReviewFormat";
import { useSmartUploadLineDraft } from "./useSmartUploadLineDraft";
import { updateSmartUploadLines } from "../../../helpers/smartUpload";
import { REVIEW_STATES } from "../../../helpers/smartUploadLineRequirements";

const CHOOSE_ANOTHER_CATEGORY = "__choose_another__";

const ALL_CATEGORY_OPTIONS = subcategories
  .filter(({ category }) => !!category)
  .map(({ subcategory, name }) => ({ subcategory, name }))
  .sort((first, second) => first.name.localeCompare(second.name));

const categoryName = (subcategoryId) =>
  ALL_CATEGORY_OPTIONS.find(({ subcategory }) => subcategory === subcategoryId)
    ?.name || subcategoryId;

const FUEL_TYPE_OPTIONS = [
  { value: "diesel", name: "Diesel" },
  { value: "gas", name: "Gasoline" }
];

const FUEL_SUBCATEGORIES = ["company-vehicles", "equipment-fuel"];

const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

// The server rejects an unparseable period (invalid_period), so the editor
// only ever shows and sends ISO YYYY-MM-DD.
const toDateInputValue = (value) => {
  if (!value) {
    return "";
  }
  if (typeof value === "string" && ISO_DATE_PREFIX.test(value)) {
    return value.slice(0, 10);
  }
  const parsed = dayjs(value?.toDate ? value.toDate() : value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const toNumberOrNull = (value) => (value === "" ? null : Number(value));

const REVIEW_STATE_LABELS = {
  pending_review: "Needs review",
  committing: "Recording…",
  committed: "Recorded",
  rejected: "Rejected",
  error: "Couldn't record",
  manual_fallback: "Moved to manual entry",
  duplicate_skipped: "Skipped"
};

const REVIEW_STATE_COLORS = {
  committed: "secondary",
  error: "error",
  rejected: "default",
  manual_fallback: "default",
  duplicate_skipped: "default"
};

const COMMIT_RESULT_COPY = {
  in_progress: "This line is already being recorded — give it a moment.",
  deferred: "Still waiting to be recorded — confirm again to finish."
};

const ROW_REASON_COPY = {
  extraction_timeout: "Reading this file took too long.",
  invalid_source_path: "We couldn't find this file's source.",
  template_shape_unsupported: "We couldn't read this file's layout.",
  invalid_entity_context: "This upload's vendor doesn't belong to this account."
};

// Mirrors functions/utils/smart-upload/appendManualLine.js's
// NON_APPENDABLE_ERROR_REASONS: a security-check failure keeps the file's
// stored path/context from being trusted at all, so "Continue with guided
// manual entry" would only fail server-side -- these keep "please
// re-upload" instead.
const NON_APPENDABLE_ERROR_REASONS = new Set([
  "invalid_source_path",
  "invalid_entity_context"
]);

const CategoryPicker = ({ candidates, onChange, disabled, label }) => {
  const [showFullList, setShowFullList] = useState(!candidates.length);
  const candidateOptions = candidates.map(({ subcategory }) => ({
    subcategory,
    name: categoryName(subcategory)
  }));
  const options = showFullList ? ALL_CATEGORY_OPTIONS : candidateOptions;

  const handleChange = ({ target: { value } }) => {
    if (value === CHOOSE_ANOTHER_CATEGORY) {
      return setShowFullList(true);
    }
    return value && onChange(value);
  };

  return (
    <TextField
      select
      size="small"
      label={label}
      value=""
      onChange={handleChange}
      disabled={disabled}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: true }}
      sx={{ minWidth: 200 }}
    >
      <option value="">
        {showFullList ? "Choose a category" : "Pick the best match"}
      </option>
      {options.map(({ subcategory, name }) => (
        <option key={subcategory} value={subcategory}>
          {name}
        </option>
      ))}
      {!showFullList && (
        <option value={CHOOSE_ANOTHER_CATEGORY}>
          Choose another category…
        </option>
      )}
    </TextField>
  );
};

const confidenceProps = (line, field, saveNow) => ({
  level: line.confidence_by_field?.[field],
  isCleared: !unclearedLowFields(line).includes(field),
  disabled: areValuesLocked(line),
  onAcknowledge: () =>
    saveNow({
      acknowledged_fields: [...new Set([...line.acknowledged_fields, field])]
    })
});

const ActivityCell = ({ draft }) => {
  const { draftLine: line, saveNow } = draft;
  const [isChanging, setIsChanging] = useState(false);
  const isLocked = areValuesLocked(line);
  const needsPick = !line.subcategory_final || line.needs_manual_categorization;
  const isFuelLine = FUEL_SUBCATEGORIES.includes(line.subcategory_final);
  const showFuelType =
    isFuelLine &&
    (line.commit_error?.reason === "fuel_type_unknown" ||
      !!line.fuel_type_final);

  const pickCategory = (subcategory) => {
    setIsChanging(false);
    return saveNow({ subcategory_final: subcategory });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {line.activity_type_ai && (
        <FieldConfidence {...confidenceProps(line, "activityType", saveNow)}>
          <Typography variant="caption" color="textSecondary">
            {line.activity_type_ai}
          </Typography>
        </FieldConfidence>
      )}
      <FieldConfidence {...confidenceProps(line, "mapping", saveNow)}>
        {!needsPick && !isChanging && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2">
              {categoryName(line.subcategory_final)}
            </Typography>
            {!isLocked && (
              <Button size="small" onClick={() => setIsChanging(true)}>
                Change
              </Button>
            )}
          </Box>
        )}
        {line.needs_manual_categorization && (
          <Typography variant="caption" color="textSecondary">
            We couldn&apos;t match this to a category.
          </Typography>
        )}
        {(needsPick || isChanging) && !isLocked && (
          <CategoryPicker
            candidates={
              line.needs_manual_categorization || isChanging
                ? []
                : line.taxonomy_candidates
            }
            onChange={pickCategory}
            label="Category"
          />
        )}
      </FieldConfidence>
      {showFuelType && (
        <TextField
          select
          size="small"
          label="Fuel type"
          value={line.fuel_type_final || ""}
          disabled={isLocked}
          onChange={({ target: { value } }) =>
            value && saveNow({ fuel_type_final: value })
          }
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
        >
          <option value="">Choose a fuel</option>
          {FUEL_TYPE_OPTIONS.map(({ value, name }) => (
            <option key={value} value={value}>
              {name}
            </option>
          ))}
        </TextField>
      )}
    </Box>
  );
};

export const ValueCell = ({
  draft,
  field,
  confidenceField,
  type = "text",
  label
}) => {
  const { draftLine: line, editFields, saveNow } = draft;
  const value = line[field];

  if (areValuesLocked(line)) {
    return <Typography variant="body2">{value ?? "—"}</Typography>;
  }

  return (
    <FieldConfidence {...confidenceProps(line, confidenceField, saveNow)}>
      <TextField
        size="small"
        type={type}
        value={value ?? ""}
        inputProps={{ "aria-label": label }}
        onChange={({ target }) =>
          editFields({
            [field]:
              type === "number" ? toNumberOrNull(target.value) : target.value
          })
        }
        sx={{ maxWidth: type === "number" ? 120 : 100 }}
      />
    </FieldConfidence>
  );
};

const PeriodCell = ({ draft }) => {
  const { draftLine: line, editFields, saveNow } = draft;
  const start = toDateInputValue(line.period_start_final);
  const end = toDateInputValue(line.period_end_final);

  if (areValuesLocked(line)) {
    return (
      <Typography variant="body2">
        {start && end ? `${formatDate(start)} – ${formatDate(end)}` : "—"}
      </Typography>
    );
  }

  return (
    <FieldConfidence {...confidenceProps(line, "period", saveNow)}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <TextField
          size="small"
          type="date"
          value={start}
          inputProps={{ "aria-label": "Period start" }}
          onChange={({ target }) =>
            editFields({ period_start_final: target.value || null })
          }
        />
        <TextField
          size="small"
          type="date"
          value={end}
          inputProps={{ "aria-label": "Period end" }}
          onChange={({ target }) =>
            editFields({ period_end_final: target.value || null })
          }
        />
      </Box>
    </FieldConfidence>
  );
};

const vendorInputValue = (line) => {
  if (line.vendor_name_final) {
    return line.vendor_name_final;
  }
  if (isVendorMatchAccepted(line)) {
    return line.vendor_match_ai.name;
  }
  return line.vendor_name_ai || "";
};

const isSuggestionPending = (line) =>
  !!line.vendor_match_ai &&
  !line.vendor_match_ai.isConfirmed &&
  !line.vendor_match_accepted &&
  !line.force_new_vendor &&
  !line.vendor_relationship_id_final;

const VendorSuggestion = ({ line, saveNow }) => {
  const {
    vendor_match_ai: { name: suggestedName }
  } = line;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography variant="caption" color="textSecondary">
        Did you mean {suggestedName}?
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        <Button
          size="small"
          onClick={() =>
            saveNow({
              vendor_match_accepted: true,
              ...(line.vendor_name_final ? { vendor_name_final: null } : {})
            })
          }
        >
          Use {suggestedName}
        </Button>
        <Button
          size="small"
          onClick={() =>
            saveNow({
              force_new_vendor: true,
              vendor_name_final: vendorInputValue(line) || null
            })
          }
        >
          Create a new vendor instead
        </Button>
      </Box>
    </Box>
  );
};

export const VendorCell = ({ draft, vendorOptions, fixedVendorName }) => {
  const { draftLine: line, editFields, saveNow } = draft;

  if (!isVendorFieldShown(line)) {
    return <Typography variant="body2">—</Typography>;
  }

  if (fixedVendorName) {
    return <Typography variant="body2">{fixedVendorName}</Typography>;
  }

  const selectedOption =
    vendorOptions.find(({ id }) => id === line.vendor_relationship_id_final) ||
    null;
  const isLocked = areValuesLocked(line);

  if (isLocked) {
    return (
      <Typography variant="body2">
        {selectedOption?.name ||
          line.vendor_name_resolved ||
          vendorInputValue(line) ||
          "—"}
      </Typography>
    );
  }

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 200 }}
    >
      <Autocomplete
        freeSolo
        size="small"
        options={vendorOptions}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.name
        }
        value={selectedOption}
        inputValue={
          selectedOption ? selectedOption.name : vendorInputValue(line)
        }
        onChange={(event, option) =>
          option && typeof option === "object"
            ? saveNow({ vendor_relationship_id_final: option.id })
            : null
        }
        onInputChange={(event, text, reason) =>
          reason === "input"
            ? editFields({
                vendor_name_final: text || null,
                ...(line.vendor_relationship_id_final
                  ? { vendor_relationship_id_final: null }
                  : {}),
                ...(line.vendor_match_accepted
                  ? { vendor_match_accepted: false }
                  : {})
              })
            : null
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              line.requires_vendor ? "Vendor (required)" : "Vendor (optional)"
            }
          />
        )}
      />
      {isSuggestionPending(line) && (
        <VendorSuggestion line={line} saveNow={saveNow} />
      )}
      {line.force_new_vendor && (
        <Typography variant="caption" color="textSecondary">
          We&apos;ll add this as a new vendor.
        </Typography>
      )}
    </Box>
  );
};

export const allocationResolutionFor = (line) => {
  const {
    allocated_entity_final: allocatedEntity,
    allocation_match_source: matchSource,
    allocation_candidates: candidates
  } = line;
  const entityId = allocatedEntity?.entity_id || null;

  if (matchSource === "matched") {
    return {
      entityId,
      matchSource,
      candidates: candidates.map(({ entity_id, label }) => ({
        entityId: entity_id,
        label
      }))
    };
  }
  if (matchSource === "context" || matchSource === "learned") {
    return { entityId, matchSource, candidates: [] };
  }
  // resolveAllocation returned null (no address or meter at all) or found
  // nothing: the line still needs an entity, so the empty picker renders
  // instead of AllocationStep's render-nothing null case.
  return { entityId, matchSource: "none", candidates: [] };
};

const EntityCell = ({ draft, documentFamily, entityNames, lineKey }) => {
  const { draftLine: line, saveNow } = draft;
  const [isChanging, setIsChanging] = useState(false);

  if (!line.requires_entity) {
    return <Typography variant="body2">—</Typography>;
  }

  const entityType = lineEntityType(line, documentFamily);
  const allocatedEntity = line.allocated_entity_final;
  const entityName = entityNames[allocatedEntity?.entity_id];
  const conflict = line.allocation_conflict;
  const isSettled = isAllocationSatisfied(line);
  const isLocked = areValuesLocked(line);
  const canChange =
    !isLocked && line.allocation_match_source !== "context" && !conflict;

  if ((isSettled && !isChanging) || isLocked) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="body2">
          {entityName ? `Added to ${entityName}` : "—"}
        </Typography>
        {canChange && (
          <Button size="small" onClick={() => setIsChanging(true)}>
            Change
          </Button>
        )}
      </Box>
    );
  }

  const confirmEntity = ({ id }) => {
    setIsChanging(false);
    const meterId =
      allocatedEntity?.meter_id || line.allocated_entity_ai?.meter_id;
    return saveNow({
      allocated_entity_final: {
        entity_type: entityType,
        entity_id: id,
        ...(meterId ? { meter_id: meterId } : {})
      },
      allocation_confirmed: true
    });
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 220 }}
    >
      {conflict && (
        <Alert severity="warning">
          This meter was previously added to{" "}
          {entityNames[conflict.learned_entity_id] || "a different location"}.
          Confirm where it belongs.
        </Alert>
      )}
      <FieldConfidence {...confidenceProps(line, "allocation", saveNow)}>
        <AllocationStep
          entityType={entityType}
          resolution={allocationResolutionFor(line)}
          onConfirm={confirmEntity}
          lineId={lineKey}
        />
      </FieldConfidence>
    </Box>
  );
};

const SourceCell = ({ line, onViewSource, canViewSource }) => {
  const location = line.source_page
    ? `Page ${line.source_page}`
    : line.source_sheet || "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {location && <Typography variant="caption">{location}</Typography>}
      {canViewSource && (
        <Button size="small" onClick={onViewSource}>
          View source
        </Button>
      )}
    </Box>
  );
};

const formatAddedAt = (value) => formatDate(csvDateValue(value));

export const duplicateNotice = ({ duplicate_of: duplicateOf }) => {
  const matchType = duplicateOf?.match_type;
  const addedOn = duplicateAddedOn(duplicateOf);
  if (matchType === "same_document" && addedOn) {
    return `You've uploaded this document before — ${
      addedOn.isConfirmed
        ? `it was added on ${formatAddedAt(addedOn.value)}`
        : `its record is dated ${formatAddedAt(addedOn.value)}`
    }. Skip or Keep both.`;
  }
  if (matchType === "same_document" && duplicateOf.file_name) {
    return `This looks like the same file as ${duplicateOf.file_name} in this upload — Skip or Keep both.`;
  }
  if (matchType === "same_batch") {
    return `This looks like the same as ${
      duplicateOf.file_name || "another line"
    } in this upload — Skip or Keep both.`;
  }
  if (addedOn?.isConfirmed) {
    return `Looks like this was already added on ${formatAddedAt(
      addedOn.value
    )} — Skip or Keep both.`;
  }
  if (addedOn) {
    return `Looks like this was already added (dated ${formatAddedAt(
      addedOn.value
    )}) — Skip or Keep both.`;
  }
  return "Looks like this was already added — Skip or Keep both.";
};

const hasValue = (value) => value !== undefined && value !== null;

const matchedAmountText = ({ tons_co2e: tons, dollar_amount: dollars }) => {
  if (hasValue(tons)) {
    return `${formatDecimal(tons)} tCO2e`;
  }
  return hasValue(dollars) ? formatDollars(dollars) : null;
};

export const matchedSummary = (matched) =>
  matched
    ? [
        matched.entity_name,
        matched.subcategory && categoryName(matched.subcategory),
        matchedAmountText(matched),
        matched.source_label
      ]
        .filter(Boolean)
        .join(" · ") || null
    : null;

// A commit that stopped on a duplicate leaves the line flagged, and the
// duplicate notice already says what happened and what to do.
const isExplainedDuplicate = ({ line, commitResult }) =>
  commitResult.reason === "duplicate_detected" && !!line.duplicate_of;

const lineMessages = ({ line, closedYears, commitResult, saveError }) =>
  [
    isLineActionable(line) &&
      isInClosedYear(line, closedYears) && {
        severity: "info",
        text: "This period is in a closed year — it'll be recorded but excluded from totals."
      },
    isLineActionable(line) &&
      !!line.duplicate_of &&
      line.duplicate_resolution !== "keep" && {
        severity: "warning",
        text: duplicateNotice(line)
      },
    isLineActionable(line) &&
      line.duplicate_resolution !== "keep" &&
      !!matchedSummary(line.duplicate_of?.matched) && {
        severity: "info",
        text: `Matches: ${matchedSummary(line.duplicate_of.matched)}`
      },
    line.review_state === REVIEW_STATES.error &&
      !!line.commit_error && {
        severity: "error",
        text: line.commit_error.message || reasonCopy(line.commit_error.reason)
      },
    line.review_state === REVIEW_STATES.committed &&
      !!line.document_filing_error && {
        severity: "warning",
        text: "Document not filed — Retry filing."
      },
    line.review_state === REVIEW_STATES.committed &&
      !!line.excluded_from_totals && {
        severity: "info",
        text: "Recorded, but excluded from totals."
      },
    line.review_state === REVIEW_STATES.committed &&
      !!line.committed_values_differ && {
        severity: "warning",
        text: "This line was already recorded with different values — the existing record was kept. Correct it from the activity record if needed."
      },
    !!commitResult &&
      isLineActionable(line) &&
      commitResult.outcome !== "committed" &&
      commitResult.outcome !== "already_committed" &&
      !isExplainedDuplicate({ line, commitResult }) && {
        severity:
          commitResult.outcome === "rejected_gate" ? "warning" : "error",
        text:
          COMMIT_RESULT_COPY[commitResult.outcome] ||
          commitResult.message ||
          reasonCopy(commitResult.reason)
      },
    !!saveError && { severity: "error", text: saveError }
  ].filter(Boolean);

export const StatusCell = ({ line, closedYears, commitResult, saveError }) => {
  const isReady = isLineReady(line);
  const label =
    line.review_state === REVIEW_STATES.pendingReview && isReady
      ? "Ready"
      : REVIEW_STATE_LABELS[line.review_state] || line.review_state;

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 0.5, maxWidth: 260 }}
    >
      <Box>
        <Chip
          size="small"
          variant="outlined"
          label={label}
          color={
            isReady
              ? "secondary"
              : REVIEW_STATE_COLORS[line.review_state] || "warning"
          }
        />
      </Box>
      {lineMessages({ line, closedYears, commitResult, saveError }).map(
        ({ severity, text }) => (
          <Typography
            key={text}
            variant="caption"
            color={severity === "error" ? "error" : "textSecondary"}
          >
            {text}
          </Typography>
        )
      )}
    </Box>
  );
};

const ConfirmButton = ({ line, onConfirm, disabled }) => {
  const { palette } = useTheme();
  const failures = lineGateFailures(line);
  const label = line.review_state === REVIEW_STATES.error ? "Retry" : "Confirm";
  const tooltip = failures.map(reasonCopy).join(" ");

  return (
    <Tooltip title={tooltip}>
      <span>
        <Button
          size="small"
          variant="contained"
          color="secondary"
          sx={{ color: palette.common.white }}
          disabled={disabled || !!failures.length}
          onClick={onConfirm}
        >
          {label}
        </Button>
      </span>
    </Tooltip>
  );
};

// Re-renders once a fresh "committing" claim goes stale, so the Retry for a
// crashed commit appears without the reviewer reopening the dialog.
const useIsClaimStale = (line) => {
  const isCommitting = line.review_state === REVIEW_STATES.committing;
  const [isStale, setIsStale] = useState(() => isClaimStale(line, Date.now()));
  const claimedAt = line.committing_claimed_at;

  useEffect(() => {
    if (!isCommitting) {
      return undefined;
    }
    const remainingMs = CLAIM_STALE_MS - claimAgeMs(line, Date.now());
    setIsStale(remainingMs < 0);
    if (remainingMs < 0) {
      return undefined;
    }
    const timeout = setTimeout(() => setIsStale(true), remainingMs + 1000);
    return () => clearTimeout(timeout);
    // Keyed on the claim itself; `line` is a fresh object every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCommitting, claimedAt]);

  return isCommitting && isStale;
};

const CommittingActions = ({ isStale, onRetry, isCommitting }) =>
  isStale ? (
    <Tooltip title="This line stopped partway through recording. Retrying is safe — it never records twice.">
      <span>
        <Button size="small" disabled={isCommitting} onClick={onRetry}>
          Retry
        </Button>
      </span>
    </Tooltip>
  ) : (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <CircularProgress size={16} />
      <Typography variant="caption">Still recording…</Typography>
    </Box>
  );

export const LineActions = ({
  draft,
  onConfirm,
  onRetryFiling,
  onEnterManually,
  isCommitting
}) => {
  const { draftLine: line, saveNow, flushNow } = draft;
  const { review_state: reviewState, is_view_only: isViewOnly } = line;
  const isStaleClaim = useIsClaimStale(line);

  if (reviewState === REVIEW_STATES.committed) {
    return line.document_filing_error ? (
      <Button size="small" onClick={onRetryFiling}>
        Retry filing
      </Button>
    ) : null;
  }

  // A manual_fallback-origin line keeps its Enter-manually/Undo CTA even in
  // finished/view-only mode: it's the one eligible way to resolve such a
  // line (ticket #42 spec, Edge Cases: "the dialog stays read-only except
  // for the manual-entry CTA on eligible rows"). Every other state stays
  // suppressed below, under the isViewOnly gate.
  if (reviewState === REVIEW_STATES.manualFallback) {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        <Button size="small" onClick={onEnterManually}>
          Enter manually
        </Button>
        <Button
          size="small"
          onClick={() => saveNow({ review_state: "pending_review" })}
        >
          Undo
        </Button>
      </Box>
    );
  }

  if (isViewOnly) {
    return null;
  }

  if (reviewState === REVIEW_STATES.committing) {
    return (
      <CommittingActions
        isStale={isStaleClaim}
        isCommitting={isCommitting}
        onRetry={() => onConfirm(line)}
      />
    );
  }

  if (UNDOABLE_REVIEW_STATES.includes(reviewState)) {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        <Button
          size="small"
          onClick={() => saveNow({ review_state: "pending_review" })}
        >
          Undo
        </Button>
      </Box>
    );
  }

  if (!isLineActionable(line)) {
    return null;
  }

  const hasUnresolvedDuplicate =
    !!line.duplicate_of && line.duplicate_resolution !== "keep";

  const confirm = async () => {
    await flushNow();
    return onConfirm(line);
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {hasUnresolvedDuplicate && (
        <>
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              saveNow({
                duplicate_resolution: "skip",
                review_state: REVIEW_STATES.duplicateSkipped
              })
            }
          >
            Skip
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => saveNow({ duplicate_resolution: "keep" })}
          >
            Keep both
          </Button>
        </>
      )}
      {!hasUnresolvedDuplicate && !line.needs_manual_categorization && (
        <ConfirmButton
          line={line}
          onConfirm={confirm}
          disabled={isCommitting}
        />
      )}
      {isManualOriginLine(line) && (
        <Button
          size="small"
          onClick={() => saveNow({ review_state: "rejected" })}
        >
          Remove entry
        </Button>
      )}
      {!isManualOriginLine(line) && (
        <>
          <Button
            size="small"
            onClick={() => saveNow({ review_state: "rejected" })}
          >
            Reject
          </Button>
          <Button size="small" onClick={onEnterManually}>
            Enter manually
          </Button>
        </>
      )}
    </Box>
  );
};

const rowSubtitle = ({ row, lineCount }) =>
  [
    DOCUMENT_FAMILY_LABELS[row.document_family] || "Document",
    row.page_count
      ? `${row.page_count} ${row.page_count === 1 ? "page" : "pages"}`
      : null,
    `${lineCount} ${lineCount === 1 ? "line" : "lines"}`
  ]
    .filter(Boolean)
    .join(" • ");

const CountChip = ({ count, label, color }) =>
  count ? (
    <Chip
      size="small"
      variant="outlined"
      color={color}
      label={`${count} ${label}`}
    />
  ) : null;

const ReadOnlyRowBody = ({
  row,
  onDismissRow,
  isDismissing,
  canDismiss,
  onOpenManualEntry
}) => {
  if (row.status === "error") {
    const canEnterManually = canDismiss && !NON_APPENDABLE_ERROR_REASONS.has(row.reason);
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          alignItems: "flex-start"
        }}
      >
        <Typography variant="body2" color="error">
          We couldn&apos;t read this file.{" "}
          {row.errorMessage || ROW_REASON_COPY[row.reason] || ""}
        </Typography>
        {canEnterManually && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => onOpenManualEntry({ row })}
          >
            Continue with guided manual entry
          </Button>
        )}
      </Box>
    );
  }

  if (row.status === "pending" || row.status === "extracting") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2">Still reading this file…</Typography>
      </Box>
    );
  }

  if (row.status === "rejected") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          alignItems: "flex-start"
        }}
      >
        <Typography variant="body2" color="textSecondary">
          Dismissed — nothing added from this file.
        </Typography>
        {canDismiss && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => onOpenManualEntry({ row })}
          >
            Continue with guided manual entry
          </Button>
        )}
      </Box>
    );
  }

  if (row.status === "extracted" || row.no_activity_found) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          alignItems: "flex-start"
        }}
      >
        <Typography variant="body2">
          We didn&apos;t find any activity to add in this file.
        </Typography>
        {canDismiss && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onOpenManualEntry({ row })}
            >
              Continue with guided manual entry
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={isDismissing}
              onClick={onDismissRow}
            >
              Nothing to add from this file
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <CircularProgress size={16} />
      <Typography variant="body2">Loading lines…</Typography>
    </Box>
  );
};

const hasLineTable = (row, lines) =>
  !!lines.length && !["error", "pending", "extracting"].includes(row.status);

// One uploaded file in the Smart Upload review dialog, rendered on the shared
// ReviewTableShell. Every column reads the line's live draft (useLineContext),
// so an edit shows at once and persists through update-lines.
const SmartUploadReviewRow = ({
  row,
  lines,
  companyId,
  taskDocId,
  entityNames,
  vendorOptions,
  fixedVendorName,
  closedYears,
  commitResults,
  isCommitting,
  onPendingChange,
  onConfirmLines,
  onRetryFiling,
  onDismissRow,
  onViewSource,
  onOpenManualEntry,
  isViewOnly = false,
  canAppendManually = true
}) => {
  const [isDismissing, setIsDismissing] = useState(false);
  const { id: rowId, document_family: documentFamily } = row;

  const readLines = lines.map((line) =>
    withDerivedRequirements(readLine(line), documentFamily)
  );
  const readyCount = isViewOnly ? 0 : readLines.filter(isLineReady).length;
  const attentionCount = readLines.filter(
    (line) =>
      line.review_state === REVIEW_STATES.error ||
      (line.review_state === REVIEW_STATES.pendingReview && !isLineReady(line))
  ).length;
  const recordedCount = readLines.filter(
    ({ review_state }) => review_state === REVIEW_STATES.committed
  ).length;
  const isNoActivity =
    row.status === "extracted" || (!!row.no_activity_found && !lines.length);
  const needsAttention =
    !!attentionCount || isNoActivity || row.status === "error";

  const saveFieldsFor = (lineId) => async (fields) => {
    const response = await updateSmartUploadLines({
      companyId,
      taskDocId,
      rowId,
      updates: [{ lineId, fields }]
    });
    const rejection = (response?.rejected || []).find(
      (rejected) => rejected.lineId === lineId
    );
    return rejection
      ? {
          error: true,
          message: rejection.message || reasonCopy(rejection.reason)
        }
      : response;
  };

  const useLineContext = (line) => {
    const draft = useSmartUploadLineDraft({
      line,
      documentFamily,
      lineKey: lineKeyFor({ rowId, lineId: line.id }),
      saveFields: saveFieldsFor(line.id),
      onPendingChange
    });
    return isViewOnly
      ? { ...draft, draftLine: { ...draft.draftLine, is_view_only: true } }
      : draft;
  };

  const columns = [
    {
      id: "activity",
      label: "Activity",
      render: (line, index, draft) => <ActivityCell draft={draft} />
    },
    {
      id: "quantity",
      label: "Quantity",
      render: (line, index, draft) => (
        <ValueCell
          draft={draft}
          field="quantity_final"
          confidenceField="quantity"
          type="number"
          label="Quantity"
        />
      )
    },
    {
      id: "unit",
      label: "Unit",
      render: (line, index, draft) => (
        <ValueCell
          draft={draft}
          field="unit_final"
          confidenceField="unit"
          label="Unit"
        />
      )
    },
    {
      id: "period",
      label: "Period",
      render: (line, index, draft) => <PeriodCell draft={draft} />
    },
    {
      id: "vendor",
      label: "Vendor",
      render: (line, index, draft) => (
        <VendorCell
          draft={draft}
          vendorOptions={vendorOptions}
          fixedVendorName={fixedVendorName}
        />
      )
    },
    {
      id: "entity",
      label: "Location",
      render: (line, index, draft) => (
        <EntityCell
          draft={draft}
          documentFamily={documentFamily}
          entityNames={entityNames}
          lineKey={lineKeyFor({ rowId, lineId: line.id })}
        />
      )
    },
    {
      id: "source",
      label: "Source",
      render: (line, index, draft) => (
        <SourceCell
          line={draft.draftLine}
          canViewSource={!!row.gcsPath}
          onViewSource={() => onViewSource(row, draft.draftLine)}
        />
      )
    },
    {
      id: "status",
      label: "Status",
      render: (line, index, draft) => (
        <StatusCell
          line={draft.draftLine}
          closedYears={closedYears}
          saveError={draft.saveError}
          commitResult={commitResults[lineKeyFor({ rowId, lineId: line.id })]}
        />
      )
    }
  ];

  const dismissRow = async () => {
    setIsDismissing(true);
    await onDismissRow(row);
    return setIsDismissing(false);
  };

  const additionalSheets = row.additional_sheets_detected || [];

  return (
    <ReviewTableShell
      title={row.fileName}
      subtitle={rowSubtitle({ row, lineCount: lines.length })}
      needsAttention={needsAttention}
      defaultExpanded={!["imported", "rejected"].includes(row.status)}
      chips={
        <>
          <CountChip count={readyCount} label="ready" color="secondary" />
          <CountChip
            count={attentionCount}
            label="need attention"
            color="warning"
          />
          <CountChip count={recordedCount} label="recorded" color="primary" />
        </>
      }
      banners={
        !!additionalSheets.length && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            This file has data on other sheets ({additionalSheets.join(", ")})
            that wasn&apos;t read — check it manually.
          </Alert>
        )
      }
      columns={columns}
      lines={lines}
      getLineKey={(line) => line.id}
      useLineContext={useLineContext}
      actionsLabel="Actions"
      renderLineActions={(line, index, draft) => (
        <LineActions
          draft={draft}
          isCommitting={isCommitting}
          onConfirm={(draftLine) =>
            onConfirmLines([{ rowId, lineId: line.id, line: draftLine }])
          }
          onRetryFiling={() => onRetryFiling({ rowId, lineId: line.id })}
          onEnterManually={() => onOpenManualEntry({ row, line })}
        />
      )}
      body={
        hasLineTable(row, lines) ? undefined : (
          <ReadOnlyRowBody
            row={row}
            onDismissRow={dismissRow}
            isDismissing={isDismissing}
            canDismiss={canAppendManually}
            onOpenManualEntry={onOpenManualEntry}
          />
        )
      }
    />
  );
};

export default SmartUploadReviewRow;
